import { useEffect, useState } from 'react'
import { buyerApi, type BuyerDelivery } from '../lib/buyer'
import { dateTime, money, num, shortDate } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { Photo } from '../ui/PhotoButton'
import { BuyerHeader } from './BuyerShell'

/**
 * What is coming and what came. The record exists so a trader can check a bill against
 * what they actually took, rather than taking anybody's word for it.
 */
export function Arriving() {
  const [rows, setRows] = useState<BuyerDelivery[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    buyerApi.get<BuyerDelivery[]>('/shop/deliveries')
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  const coming = rows.filter((r) => !['delivered', 'part_delivered', 'failed'].includes(r.status))
  const arrived = rows.filter((r) => !coming.includes(r))
  const takenKg = arrived.reduce((s, r) => s + Number(r.acceptedKg ?? 0), 0)
  const sentBack = arrived.reduce((s, r) => s + Number(r.rejectedKg ?? 0), 0)

  return (
    <>
      <BuyerHeader />
      <div className="field-greet">
        <h1>Arriving</h1>
        <p>What is on its way, and what you signed for.</p>
      </div>

      <div className="field-body">
        {loading && <Panel><Loading rows={4} /></Panel>}

        {!loading && rows.length === 0 && (
          <Empty title="Nothing on the way">
            Once an order is packed onto a van you will see the day and the driver here.
          </Empty>
        )}

        {arrived.length > 0 && (
          <div className="mini-row">
            <StatMini label="Deliveries" value={arrived.length} />
            <StatMini label="You took" value={`${num(takenKg, 0)} kg`} />
            <StatMini label="You sent back" value={`${num(sentBack, 0)} kg`}
              note={sentBack > 0 ? 'not billed to you' : 'none'} />
          </div>
        )}

        {coming.length > 0 && (
          <Panel title="On the way" meta={`${coming.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
              {coming.map((d) => (
                <div key={d.id} style={{
                  border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
                  padding: 'var(--sp-6) var(--sp-7)', background: 'var(--surface)',
                }}>
                  <div className="spread">
                    <b>{d.plannedAt ? shortDate(d.plannedAt) : 'Being planned'}</b>
                    <Pill tone={d.status === 'en_route' ? 'ok' : 'info'} dot={false}>
                      {d.status === 'en_route' ? 'On the road' : 'Being packed'}
                    </Pill>
                  </div>
                  <div className="faint" style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}>
                    <span className="mono">{d.orderCode}</span>
                    {d.driverName && <> · {d.driverName} is bringing it</>}
                  </div>
                  {d.lines.length > 0 && (
                    <div style={{ marginTop: 'var(--sp-5)' }}>
                      {d.lines.map((l, i) => (
                        <div className="spread" key={i} style={{ fontSize: 'var(--fs-sm)', padding: '2px 0' }}>
                          <span className="muted">{l.cropName} {l.gradeCode}</span>
                          <span className="num">{num(l.acceptedKg || 0, 0)} kg</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        )}

        {arrived.length > 0 && (
          <Panel title="Already delivered" meta={`${arrived.length}`}>
            <div className="panel-body" style={{ paddingTop: 4, paddingBottom: 4 }}>
              {arrived.map((d) => {
                const isOpen = open === d.id
                const back = Number(d.rejectedKg) > 0
                return (
                  <div key={d.id}>
                    <button className="stop-row" style={{ width: '100%' }}
                      onClick={() => setOpen(isOpen ? null : d.id)}>
                      <span className={`stop-state ${back ? 'pending' : 'done'}`}>
                        {back ? '!' : '✓'}
                      </span>
                      <span className="stop-who">
                        <b>{num(d.acceptedKg, 0)} kg taken</b>
                        <i>
                          {d.completedAt ? shortDate(d.completedAt) : '—'}
                          {d.signedBy && ` · signed by ${d.signedBy}`}
                        </i>
                      </span>
                      <span className="stop-amt">
                        <b>{back ? `${num(d.rejectedKg, 0)} kg back` : ''}</b>
                        <span className="mono">{d.code}</span>
                      </span>
                    </button>

                    {isOpen && (
                      <div style={{
                        background: 'var(--surface-2)', border: '1px solid var(--line-2)',
                        borderRadius: 'var(--r-md)', padding: 'var(--sp-6) var(--sp-7)',
                        margin: '0 0 var(--sp-6)',
                      }}>
                        <div className="micro" style={{ marginBottom: 'var(--sp-5)' }}>
                          What you took
                        </div>
                        {d.lines.map((l, i) => (
                          <div className="spread" key={i}
                            style={{ fontSize: 'var(--fs-sm)', padding: '3px 0' }}>
                            <span className="muted">
                              {l.cropName} {l.gradeCode}
                              {Number(l.rejectedKg) > 0 && (
                                <span className="crit-t"> · {num(l.rejectedKg, 1)} kg sent back</span>
                              )}
                            </span>
                            <b className="num">
                              {num(l.acceptedKg, 1)} kg
                              {l.unitPrice !== null && (
                                <span className="faint">
                                  {' '}· {money(Number(l.acceptedKg) * Number(l.unitPrice), { currency: false })}
                                </span>
                              )}
                            </b>
                          </div>
                        ))}
                        <div className="divider" style={{ margin: 'var(--sp-5) 0' }} />
                        <div className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
                          <span className="muted">Signed by</span>
                          <b>{d.signedBy ?? '—'}{d.signedRole ? `, ${d.signedRole}` : ''}</b>
                        </div>
                        <div className="spread" style={{ fontSize: 'var(--fs-sm)' }}>
                          <span className="muted">When</span>
                          <b>{d.completedAt ? dateTime(d.completedAt) : '—'}</b>
                        </div>

                        {/*
                          The mark somebody at your gate made, shown back to you. This is
                          the point of taking it: a name typed by the driver is the
                          driver's account of who accepted the load, and this is not.
                        */}
                        {d.signatureRef && (
                          <div style={{ marginTop: 'var(--sp-5)' }}>
                            <div className="micro" style={{ marginBottom: 'var(--sp-3)' }}>
                              Signature taken at your gate
                            </div>
                            <div style={{
                              background: '#fff', border: '1px solid var(--line-2)',
                              borderRadius: 'var(--r-sm)', padding: 'var(--sp-3)',
                            }}>
                              <Photo refValue={d.signatureRef} alt="Signature on delivery" />
                            </div>
                          </div>
                        )}

                        {d.photoRef && (
                          <div style={{ marginTop: 'var(--sp-5)' }}>
                            <div className="micro" style={{ marginBottom: 'var(--sp-3)' }}>
                              The load as it arrived
                            </div>
                            <Photo refValue={d.photoRef} alt="The delivery" />
                          </div>
                        )}
                        {back && (
                          <p className="hint" style={{ marginTop: 'var(--sp-5)' }}>
                            What you sent back is not on your bill.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Panel>
        )}

        {rows.length > 0 && (
          <Banner tone="info" title="You are billed for what you take, not what was loaded">
            Whatever you turn away at the door comes off the invoice before it is raised.
          </Banner>
        )}
      </div>
    </>
  )
}
